export type LocaleMessages = Record<string, unknown>;

export function completeLocaleMessages(
  english: LocaleMessages,
  overrides: LocaleMessages,
  generated: Record<string, string>
): LocaleMessages {
  const completed = mergeLocaleValue(english, overrides) as LocaleMessages;
  for (const [pointer, value] of Object.entries(generated)) {
    setJsonPointer(completed, pointer, value);
  }
  return completed;
}

function mergeLocaleValue(base: unknown, override: unknown): unknown {
  if (Array.isArray(base)) {
    if (!Array.isArray(override)) return base.map((value) => mergeLocaleValue(value, undefined));
    return base.map((value, index) => mergeLocaleValue(value, override[index]));
  }

  if (isRecord(base)) {
    const localized = isRecord(override) ? override : {};
    return Object.fromEntries(
      Object.entries(base).map(([key, value]) => [key, mergeLocaleValue(value, localized[key])])
    );
  }

  return override === undefined ? base : override;
}

function setJsonPointer(target: LocaleMessages, pointer: string, value: string): void {
  const segments = parseJsonPointer(pointer);
  if (segments.length === 0) throw new Error('A generated locale entry cannot target the root.');

  let cursor: unknown = target;
  for (const segment of segments.slice(0, -1)) {
    if (Array.isArray(cursor)) {
      const index = parseArrayIndex(segment, pointer);
      cursor = cursor[index];
    } else if (isRecord(cursor)) {
      cursor = cursor[segment];
    } else {
      throw new Error(`Generated locale pointer does not match the English tree: ${pointer}`);
    }
  }

  const leaf = segments.at(-1)!;
  if (Array.isArray(cursor)) {
    cursor[parseArrayIndex(leaf, pointer)] = value;
  } else if (isRecord(cursor)) {
    cursor[leaf] = value;
  } else {
    throw new Error(`Generated locale pointer does not match the English tree: ${pointer}`);
  }
}

function parseJsonPointer(pointer: string): string[] {
  if (!pointer.startsWith('/')) throw new Error(`Invalid generated locale pointer: ${pointer}`);
  return pointer
    .slice(1)
    .split('/')
    .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
}

function parseArrayIndex(segment: string, pointer: string): number {
  if (!/^\d+$/.test(segment)) {
    throw new Error(`Generated locale pointer has an invalid array index: ${pointer}`);
  }
  return Number(segment);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
